import { useMemo, useState } from "react";
import { Box, IconButton, Paper, TextField } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Menu } from "components/common/Menu";
import { MoreVert } from "@mui/icons-material";
import useUsers from "hooks/users/useUsers";
import { User } from "types/user";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import { surfacePanelSx, portalDataGridSx } from "constants/pageSurfaces";

function userMatchesQuery(user: User, q: string) {
  const hay = [user.firstName, user.lastName, user.email, user.phoneNumber].filter(Boolean).join(" ").toLowerCase();
  return hay.includes(q);
}

function UserManagementTable() {
  const { users, deleteUser } = useUsers();
  const [searchQuery, setSearchQuery] = useState("");
  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => userMatchesQuery(u, q));
  }, [users, searchQuery]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "firstName",
        headerName: "First name",
        minWidth: 120,
        flex: 0.7,
      },
      {
        field: "lastName",
        headerName: "Last name",
        minWidth: 120,
        flex: 0.7,
      },
      {
        field: "email",
        headerName: "Email address",
        minWidth: 200,
        flex: 1.2,
      },
      {
        field: "phoneNumber",
        headerName: "Phone number",
        minWidth: 130,
        flex: 0.8,
      },
      {
        field: "actions",
        headerName: "",
        type: "actions",
        width: 64,
        align: "right",
        headerAlign: "right",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Menu
            button={(props: Record<string, unknown>) => (
              <span>
                <IconButton {...props} size="small">
                  <MoreVert />
                </IconButton>
              </span>
            )}
            actions={[
              {
                label: "Edit User",
                to: `${(params.row as User).uuid}/edit`,
              },
              {
                label: (
                  <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DeleteOutlineIcon fontSize="small" sx={{ color: "error.main" }} />
                    Delete user
                  </Box>
                ),
                onClick: () => {
                  deleteUser((params.row as User).uuid);
                },
                props: { sx: { color: "error.main" } },
              },
            ]}
          />
        ),
      },
    ],
    [deleteUser]
  );

  const noRowsMessage = users.length === 0 ? "No users yet." : "No users match your search.";

  return (
    <Box sx={{ mt: 2 }}>
      <TextField
        size="small"
        placeholder="Search users…"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{
          mb: 2,
          minWidth: { xs: "100%", sm: 280 },
          maxWidth: 400,
          "& .MuiOutlinedInput-root": { bgcolor: "#ffffff" },
        }}
      />
      <Paper variant="outlined" sx={{ ...surfacePanelSx, width: "100%", overflow: "hidden" }}>
        <DataGridPro
          autoHeight
          disableColumnMenu
          rows={filteredUsers}
          columns={columns}
          getRowId={(row) => row.uuid}
          rowHeight={52}
          sx={portalDataGridSx}
          initialState={{ pinnedColumns: { right: ["actions"] } }}
          hideFooter
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>{noRowsMessage}</Box>
            ),
          }}
        />
      </Paper>
    </Box>
  );
}

export default UserManagementTable;
